import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {User, Save, Loader2, CheckCircle2, AlertCircle, Upload, X, FileText, Move, ZoomIn} from 'lucide-react'
import ColaboradorLayout from '@/components/ColaboradorLayout'
import { lumi } from '@/lib/lumi'
import { useAuth } from '@/hooks/useAuth'
import { checkCollaboratorDuplicate } from '@/lib/duplicateDetection'

const EDUCATION_OPTIONS = [
  'Secundaria', 'Bachillerato / Preparatoria', 'Técnico', 'Licenciatura',
  'Maestría', 'Doctorado', 'Otro',
]

// Redes profesionales para colaboradores
const PROFESSIONAL_NETWORKS = [
  { key: 'linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/in/tuperfil', color: 'bg-blue-700', icon: 'in' },
  { key: 'github', label: 'GitHub', placeholder: 'https://github.com/tuusuario', color: 'bg-slate-800', icon: 'gh' },
  { key: 'behance', label: 'Behance', placeholder: 'https://behance.net/tuperfil', color: 'bg-blue-600', icon: 'be' },
  { key: 'twitter', label: 'X / Twitter', placeholder: 'https://x.com/tuusuario', color: 'bg-slate-900', icon: 'x' },
  { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/tuusuario', color: 'bg-pink-600', icon: 'ig' },
  { key: 'youtube', label: 'YouTube', placeholder: 'https://youtube.com/@tucanal', color: 'bg-red-600', icon: 'yt' },
  { key: 'portfolio', label: 'Portafolio web', placeholder: 'https://miportafolio.com', color: 'bg-amber-600', icon: 'pf' },
]

type ProfLinks = Record<string, string>

function ColaboradorPerfil() {
  const { user, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const photoInputRef = useRef<HTMLInputElement>(null)
  const cvInputRef = useRef<HTMLInputElement>(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState('')
  const [profileId, setProfileId] = useState<string | null>(null)
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const [uploadingCv, setUploadingCv] = useState(false)
  const [photoOffset, setPhotoOffset] = useState({ x: 50, y: 50 })
  const [isDraggingPhoto, setIsDraggingPhoto] = useState(false)
  const photoDragRef = useRef<{ startX: number; startY: number; ox: number; oy: number } | null>(null)
  const photoContainerRef = useRef<HTMLDivElement>(null)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [activeNetwork, setActiveNetwork] = useState<string | null>(null)
  const [profLinks, setProfLinks] = useState<ProfLinks>({})
  const [form, setForm] = useState({
    fullName: '', birthDate: '', email: '', phone: '',
    address: '', city: '', state: '', education: '',
    skills: '', certifications: '', languages: '',
    experience: '',
    cvUrl: '',
    salaryExpectation: '', photoUrl: '',
  })

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return }
    const load = async () => {
      if (!user) return
      try {
        const res = await lumi.entities.collaborators.list({ filter: { userId: user.userId }, limit: 1 })
        if (res.list.length > 0) {
          const c = res.list[0]
          setProfileId(c._id)
          setForm({
            fullName: c.fullName || '', birthDate: c.birthDate ? c.birthDate.split('T')[0] : '',
            email: c.email || user.email, phone: c.phone || '',
            address: c.address || '', city: c.city || '', state: c.state || '',
            education: c.education || '',
            skills: Array.isArray(c.skills) ? c.skills.join(', ') : '',
            certifications: Array.isArray(c.certifications) ? c.certifications.join(', ') : '',
            languages: Array.isArray(c.languages) ? c.languages.join(', ') : '',
            experience: c.experience || '',
            cvUrl: c.cvUrl || '',
            salaryExpectation: c.salaryExpectation ? String(c.salaryExpectation) : '',
            photoUrl: c.photoUrl || '',
          })
          if (c.photoOffset && typeof c.photoOffset === 'object') {
            setPhotoOffset(c.photoOffset as { x: number; y: number })
          }
          // Cargar redes profesionales guardadas
          if (c.professionalLinks && typeof c.professionalLinks === 'object') {
            setProfLinks(c.professionalLinks as ProfLinks)
          } else if (typeof c.professionalLinks === 'string' && c.professionalLinks) {
            setProfLinks({ linkedin: c.professionalLinks })
          }
        } else {
          setForm((f) => ({ ...f, email: user.email }))
        }
      } catch (e) { console.error(e) }
    }
    load()
  }, [isAuthenticated, user])

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }))
  const toArray = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean)

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadingPhoto(true)
    try {
      const results = await lumi.tools.file.upload([file])
      const result = results[0]
      if (result.fileUrl) {
        set('photoUrl', result.fileUrl)
      } else {
        setError(result.uploadError || 'Error al subir la foto.')
      }
    } catch (err: any) {
      setError(err?.message || 'Error al subir la foto.')
    } finally {
      setUploadingPhoto(false)
      if (photoInputRef.current) photoInputRef.current.value = ''
    }
  }

  const handleCvUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadingCv(true)
    try {
      const results = await lumi.tools.file.upload([file])
      const result = results[0]
      if (result.fileUrl) {
        set('cvUrl', result.fileUrl)
      } else {
        setError(result.uploadError || 'Error al subir el CV.')
      }
    } catch (err: any) {
      setError(err?.message || 'Error al subir el CV.')
    } finally {
      setUploadingCv(false)
      if (cvInputRef.current) cvInputRef.current.value = ''
    }
  }

  const setProfLink = (key: string, value: string) => {
    setProfLinks((prev) => ({ ...prev, [key]: value }))
  }

  const removeProfLink = (key: string) => {
    setProfLinks((prev) => { const next = { ...prev }; delete next[key]; return next })
    if (activeNetwork === key) setActiveNetwork(null)
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user) return
    setError(''); setSaving(true); setSaved(false)
    try {
      const payload = {
        userId: user.userId, fullName: form.fullName,
        birthDate: form.birthDate ? new Date(form.birthDate).toISOString() : undefined,
        email: form.email, phone: form.phone, address: form.address,
        city: form.city, state: form.state, education: form.education,
        skills: toArray(form.skills), certifications: toArray(form.certifications),
        languages: toArray(form.languages), experience: form.experience,
        cvUrl: form.cvUrl,
        professionalLinks: profLinks,
        salaryExpectation: form.salaryExpectation ? parseFloat(form.salaryExpectation) : undefined,
        photoUrl: form.photoUrl, updatedAt: new Date().toISOString(),
        photoOffset,
      }
      if (profileId) {
        await lumi.entities.collaborators.update(profileId, payload)
      } else {
        const created = await lumi.entities.collaborators.create({ ...payload, createdAt: new Date().toISOString() })
        setProfileId(created._id)
        const profRes = await lumi.entities.profiles.list({ filter: { userId: user.userId }, limit: 1 })
        if (profRes.list.length > 0) {
          await lumi.entities.profiles.update(profRes.list[0]._id, { onboardingCompleted: true, displayName: form.fullName, updatedAt: new Date().toISOString() })
        }
        // Detecta posibles colaboradores duplicados y notifica al admin para revisión/anulación
        await checkCollaboratorDuplicate({
          userId: user.userId,
          fullName: form.fullName,
          email: form.email,
          phone: form.phone,
        })
      }
      setSaved(true)
      setTimeout(() => {
        setSaved(false)
        navigate('/colaborador/dashboard')
      }, 1200)
    } catch (e: any) {
      setError(e?.message || 'Error al guardar. Intenta de nuevo.')
    } finally {
      setSaving(false)
    }
  }

  const inputCls = 'w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm text-slate-900 placeholder-slate-400 outline-none transition focus:border-amber-400 focus:ring-2 focus:ring-amber-100'
  const labelCls = 'mb-1.5 block text-sm font-medium text-slate-700'

  const activeNetConfig = activeNetwork ? PROFESSIONAL_NETWORKS.find((n) => n.key === activeNetwork) : null

  // Drag handlers para reposicionar la foto de perfil
  const handlePhotoDragStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!form.photoUrl) return
    e.preventDefault()
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY
    photoDragRef.current = { startX: clientX, startY: clientY, ox: photoOffset.x, oy: photoOffset.y }
    setIsDraggingPhoto(true)
  }, [form.photoUrl, photoOffset])

  const handlePhotoDragMove = useCallback((e: MouseEvent | TouchEvent) => {
    if (!photoDragRef.current || !photoContainerRef.current) return
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY
    const rect = photoContainerRef.current.getBoundingClientRect()
    const dx = clientX - photoDragRef.current.startX
    const dy = clientY - photoDragRef.current.startY
    const newX = Math.max(0, Math.min(100, photoDragRef.current.ox + (dx / rect.width) * 100))
    const newY = Math.max(0, Math.min(100, photoDragRef.current.oy + (dy / rect.height) * 100))
    setPhotoOffset({ x: newX, y: newY })
  }, [])

  const handlePhotoDragEnd = useCallback(() => {
    photoDragRef.current = null
    setIsDraggingPhoto(false)
  }, [])

  // Detecta clic simple (sin arrastre) sobre la foto para abrir el lightbox
  const photoMouseDownPos = useRef<{ x: number; y: number } | null>(null)
  const handlePhotoClickCapture = useCallback((e: React.MouseEvent) => {
    if (!form.photoUrl) return
    if (!photoMouseDownPos.current) return
    const dx = Math.abs(e.clientX - photoMouseDownPos.current.x)
    const dy = Math.abs(e.clientY - photoMouseDownPos.current.y)
    photoMouseDownPos.current = null
    // Si apenas se movió el cursor, lo tratamos como clic -> abrir lightbox
    if (dx < 5 && dy < 5) setLightboxOpen(true)
  }, [form.photoUrl])

  useEffect(() => {
    if (isDraggingPhoto) {
      window.addEventListener('mousemove', handlePhotoDragMove)
      window.addEventListener('mouseup', handlePhotoDragEnd)
      window.addEventListener('touchmove', handlePhotoDragMove, { passive: false })
      window.addEventListener('touchend', handlePhotoDragEnd)
    }
    return () => {
      window.removeEventListener('mousemove', handlePhotoDragMove)
      window.removeEventListener('mouseup', handlePhotoDragEnd)
      window.removeEventListener('touchmove', handlePhotoDragMove)
      window.removeEventListener('touchend', handlePhotoDragEnd)
    }
  }, [isDraggingPhoto, handlePhotoDragMove, handlePhotoDragEnd])

  return (
    <ColaboradorLayout
      title="Mi perfil profesional"
      subtitle="Completa tu información para destacar ante las empresas"
    >
      <div className="mx-auto max-w-3xl">
        <form onSubmit={handleSave} className="space-y-6">

          {/* Foto de perfil */}
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <h2 className="mb-5 text-sm font-semibold uppercase tracking-wider text-slate-400">Foto de perfil</h2>
            <div className="flex items-start gap-5">
              <div
                ref={photoContainerRef}
                className={`relative flex h-24 w-24 shrink-0 items-center justify-center rounded-full border-2 border-dashed border-slate-300 bg-slate-50 overflow-hidden select-none ${form.photoUrl ? (isDraggingPhoto ? 'cursor-grabbing' : 'cursor-grab') : ''}`}
                onMouseDown={(e) => { photoMouseDownPos.current = { x: e.clientX, y: e.clientY }; handlePhotoDragStart(e) }}
                onTouchStart={handlePhotoDragStart}
                onClick={handlePhotoClickCapture}
                title={form.photoUrl ? 'Clic para ampliar · arrastra para reposicionar' : ''}
              >
                {form.photoUrl ? (
                  <>
                    <img
                      src={form.photoUrl}
                      alt="Foto"
                      draggable={false}
                      className="absolute h-full w-full"
                      style={{
                        objectFit: 'cover',
                        objectPosition: `${photoOffset.x}% ${photoOffset.y}%`,
                      }}
                    />
                    <button
                      type="button"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={() => { set('photoUrl', ''); setPhotoOffset({ x: 50, y: 50 }) }}
                      className="absolute top-0.5 right-0.5 z-10 rounded-full bg-white/90 p-0.5 text-slate-500 shadow hover:text-red-600"
                    >
                      <X size={13} />
                    </button>
                    <button
                      type="button"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => { e.stopPropagation(); setLightboxOpen(true) }}
                      className="absolute bottom-0.5 right-0.5 z-10 rounded-full bg-black/50 p-0.5 text-white hover:bg-black/70"
                      title="Ver foto"
                    >
                      <ZoomIn size={10} />
                    </button>
                    <div className="absolute bottom-0.5 left-0.5 z-10 rounded-full bg-black/40 p-0.5">
                      <Move size={10} className="text-white" />
                    </div>
                  </>
                ) : (
                  <User size={36} className="text-slate-300" />
                )}
              </div>
              <div className="flex-1">
                <p className="mb-3 text-sm text-slate-600">Sube tu foto de perfil. Formatos: PNG, JPG, WEBP. Máx. 10 MB.</p>
                {form.photoUrl && (
                  <p className="mb-2 text-xs text-slate-400 flex items-center gap-1">
                    <Move size={11} /> Arrastra la foto para centrar o reposicionar
                  </p>
                )}
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={handlePhotoUpload}
                />
                <button
                  type="button"
                  disabled={uploadingPhoto}
                  onClick={() => photoInputRef.current?.click()}
                  className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
                >
                  {uploadingPhoto ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
                  {uploadingPhoto ? 'Subiendo...' : form.photoUrl ? 'Cambiar foto' : 'Seleccionar foto'}
                </button>
                {form.photoUrl && (
                  <p className="mt-2 text-xs text-emerald-600 flex items-center gap-1">
                    <CheckCircle2 size={12} /> Foto cargada correctamente
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Datos personales */}
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <h2 className="mb-5 text-sm font-semibold uppercase tracking-wider text-slate-400">Datos personales</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className={labelCls}>Nombre completo *</label>
                <input className={inputCls} value={form.fullName} onChange={(e) => set('fullName', e.target.value)} placeholder="Tu nombre completo" required />
              </div>
              <div>
                <label className={labelCls}>Fecha de nacimiento</label>
                <input type="date" className={inputCls} value={form.birthDate} onChange={(e) => set('birthDate', e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Teléfono</label>
                <input className={inputCls} value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+52 000 000 0000" />
              </div>
              <div>
                <label className={labelCls}>Correo</label>
                <input type="email" className={inputCls} value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="correo@ejemplo.com" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Dirección</label>
                <input className={inputCls} value={form.address} onChange={(e) => set('address', e.target.value)} placeholder="Calle, número, colonia" />
              </div>
              <div>
                <label className={labelCls}>Ciudad</label>
                <input className={inputCls} value={form.city} onChange={(e) => set('city', e.target.value)} placeholder="Ciudad" />
              </div>
              <div>
                <label className={labelCls}>Estado</label>
                <input className={inputCls} value={form.state} onChange={(e) => set('state', e.target.value)} placeholder="Estado" />
              </div>
            </div>
          </div>

          {/* Formación y habilidades */}
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <h2 className="mb-5 text-sm font-semibold uppercase tracking-wider text-slate-400">Formación y habilidades</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelCls}>Escolaridad</label>
                <select className={inputCls} value={form.education} onChange={(e) => set('education', e.target.value)}>
                  <option value="">Seleccionar nivel</option>
                  {EDUCATION_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Pretención salarial mensual (MXN)</label>
                <input type="number" className={inputCls} value={form.salaryExpectation} onChange={(e) => set('salaryExpectation', e.target.value)} placeholder="Ej. 18000" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Habilidades <span className="font-normal text-slate-400">(separadas por comas)</span></label>
                <input className={inputCls} value={form.skills} onChange={(e) => set('skills', e.target.value)} placeholder="Excel, Liderazgo, Ventas, Atención al cliente, React…" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>
                  Cursos y certificaciones
                  <span className="font-normal text-slate-400"> (separados por comas)</span>
                </label>
                <p className="mb-1.5 text-xs text-slate-400">Anota los cursos, diplomados o certificaciones que hayas obtenido fuera de tu carrera formal. Ej: Google Analytics, Excel Avanzado, Curso de ventas, etc.</p>
                <input className={inputCls} value={form.certifications} onChange={(e) => set('certifications', e.target.value)} placeholder="Google Analytics, Excel Avanzado, Curso de ventas, PMP…" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Idiomas <span className="font-normal text-slate-400">(separados por comas)</span></label>
                <input className={inputCls} value={form.languages} onChange={(e) => set('languages', e.target.value)} placeholder="Español, Inglés, Francés" />
              </div>
            </div>
          </div>

          {/* Experiencia y documentos */}
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <h2 className="mb-5 text-sm font-semibold uppercase tracking-wider text-slate-400">Experiencia y CV</h2>
            <div className="space-y-4">
              <div>
                <label className={labelCls}>Experiencia laboral</label>
                <textarea rows={4} className={inputCls} value={form.experience} onChange={(e) => set('experience', e.target.value)} placeholder="Describe tu experiencia, puestos, empresas y logros..." />
              </div>

              {/* Upload CV */}
              <div>
                <label className={labelCls}>Currículum Vitae (PDF)</label>
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    {form.cvUrl ? (
                      <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5">
                        <FileText size={16} className="text-emerald-600 shrink-0" />
                        <a
                          href={form.cvUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 truncate text-sm text-emerald-700 font-medium hover:underline"
                        >
                          Ver CV cargado
                        </a>
                        <button type="button" onClick={() => set('cvUrl', '')} className="ml-1 text-slate-400 hover:text-red-600">
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 pt-2">No hay CV cargado aún.</p>
                    )}
                  </div>
                  <div className="shrink-0">
                    <input
                      ref={cvInputRef}
                      type="file"
                      accept="application/pdf"
                      className="hidden"
                      onChange={handleCvUpload}
                    />
                    <button
                      type="button"
                      disabled={uploadingCv}
                      onClick={() => cvInputRef.current?.click()}
                      className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
                    >
                      {uploadingCv ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
                      {uploadingCv ? 'Subiendo...' : form.cvUrl ? 'Reemplazar CV' : 'Subir CV (PDF)'}
                    </button>
                  </div>
                </div>
                <p className="mt-1.5 text-xs text-slate-400">Solo archivos PDF. Máx. 10 MB.</p>
              </div>
            </div>
          </div>

          {/* Redes profesionales */}
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <h2 className="mb-1 text-sm font-semibold uppercase tracking-wider text-slate-400">Redes profesionales</h2>
            <p className="mb-4 text-xs text-slate-500">Selecciona una red y pega el enlace de tu perfil.</p>

            {/* Botones de redes */}
            <div className="mb-4 flex flex-wrap gap-2">
              {PROFESSIONAL_NETWORKS.map((net) => {
                const hasLink = !!profLinks[net.key]
                return (
                  <button
                    key={net.key}
                    type="button"
                    onClick={() => setActiveNetwork(activeNetwork === net.key ? null : net.key)}
                    className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-white transition
                      ${net.color} ${activeNetwork === net.key ? 'ring-2 ring-offset-1 ring-amber-400' : ''}
                      ${hasLink ? 'opacity-100' : 'opacity-60 hover:opacity-90'}`}
                  >
                    <span className="uppercase font-bold">{net.icon}</span>
                    {net.label}
                    {hasLink && <CheckCircle2 size={12} className="ml-0.5" />}
                  </button>
                )
              })}
            </div>

            {/* Campo activo */}
            {activeNetConfig && (
              <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-bold text-white ${activeNetConfig.color}`}>
                  {activeNetConfig.icon.toUpperCase()}
                </span>
                <input
                  className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 outline-none transition focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                  value={profLinks[activeNetConfig.key] || ''}
                  onChange={(e) => setProfLink(activeNetConfig.key, e.target.value)}
                  placeholder={activeNetConfig.placeholder}
                />
                {profLinks[activeNetConfig.key] && (
                  <button type="button" onClick={() => removeProfLink(activeNetConfig.key)} className="rounded-lg p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50">
                    <X size={15} />
                  </button>
                )}
              </div>
            )}

            {/* Resumen de links activos */}
            {Object.keys(profLinks).filter((k) => profLinks[k]).length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {Object.entries(profLinks).filter(([, v]) => v).map(([key, url]) => {
                  const net = PROFESSIONAL_NETWORKS.find((n) => n.key === key)
                  if (!net) return null
                  return (
                    <a
                      key={key}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-700 hover:bg-slate-200 transition"
                    >
                      <span className={`inline-flex h-4 w-4 items-center justify-center rounded-full text-white font-bold ${net.color}`} style={{ fontSize: 8 }}>
                        {net.icon.toUpperCase()}
                      </span>
                      {net.label}
                    </a>
                  )
                })}
              </div>
            )}
          </div>

          {error && (
            <div className="flex items-start gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertCircle size={15} className="mt-0.5 shrink-0" /><span>{error}</span>
            </div>
          )}

          <div className="flex items-center justify-between">
            {saved && (
              <span className="flex items-center gap-1.5 text-sm text-emerald-600">
                <CheckCircle2 size={16} /> Cambios guardados correctamente
              </span>
            )}
            <div className="ml-auto">
              <button type="submit" disabled={saving} className="flex items-center gap-2 rounded-lg bg-slate-900 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-60">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                {saving ? 'Guardando...' : 'Guardar perfil'}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Lightbox foto de perfil */}
      {lightboxOpen && form.photoUrl && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4"
          onClick={() => setLightboxOpen(false)}
        >
          <div className="relative max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setLightboxOpen(false)}
              className="absolute -top-10 right-0 flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-sm text-white hover:bg-white/20"
            >
              <X size={15} /> Cerrar
            </button>
            <img
              src={form.photoUrl}
              alt="Foto de perfil"
              className="mx-auto max-h-[80vh] w-auto rounded-2xl shadow-2xl"
            />
          </div>
        </div>
      )}
    </ColaboradorLayout>
  )
}

export default ColaboradorPerfil
